import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { toasterMessages } from '@messages/*';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription } from 'rxjs';
import { LoginRegisterService } from 'src/app/login-register/login-register.service';
import { WindowRefService } from 'src/app/window-ref.service';
import { HomeService } from '../../home.service';
import { environment } from 'src/environments/environment';
import { DataService } from 'src/app/services/data.service';
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';
declare let Razorpay: any;

@Component({
  selector: 'app-pricing-cards',
  templateUrl: './pricing-cards.component.html',
  styleUrls: ['./pricing-cards.component.scss'],
  animations: [
    trigger('posX', [
      state('MONTHLY', style({ transform: 'translateX(0)' })),
      state('YEARLY', style({ transform: 'translateX(110px)' })),
      transition('* => MONTHLY', animate(200)),
      transition('* => YEARLY', animate(200)),
    ])
  ]
})
export class PricingCardsComponent implements OnInit, OnChanges {

  @Input() prices;
  @Output() moreClick = new EventEmitter<any>();
  @Output() togglePlan = new EventEmitter<any>();
  private subs = new Subscription();
  userDetails: any;
  priceToggle: boolean = false;

  pos: string = 'MONTHLY';
  pos2: string = 'YEARLY';
  paymentGatewayKeyId: string | undefined;
  discountLabel: string = '';
  private offerId: string = '';
  minorUnitDivisor: number = 100;
  private lastPreviewKey: string = '';
  // Values from preview for UI display
  discountedPayableMinor: number | null = null;
  originalAmountMinor: number | null = null;
  constructor(
    private router: Router,
    private loginRegisterService: LoginRegisterService,
    private ngxService: NgxUiLoaderService,
    private homeService: HomeService,
    private winRef: WindowRefService,
    private dataService: DataService,
    private ngZone: NgZone,
    private toastr: ToastrService) { }

  ngOnInit(): void {
    this.subs.add(
      this.loginRegisterService.currentUser.subscribe(res => {
        if (res) {
          this.userDetails = res;
        } else {
          this.userDetails = null;
        }
      })
    )
    this.updateDivisorFromCurrency();
    this.updateDiscountLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.prices) {
      // Sync offer to plan period when input changes
      const period = (this.prices?.period || '').toUpperCase();
      const envYearly = (window as any)?.__env?.YEARLY_OFFER_ID || (environment as any)?.YEARLY_OFFER_ID;
      const envMonthly = (window as any)?.__env?.MONTHLY_OFFER_ID || (environment as any)?.MONTHLY_OFFER_ID;
      if (period === 'YEARLY' && envYearly) {
        this.offerId = envYearly;
      } else if (period === 'MONTHLY' && envMonthly) {
        this.offerId = envMonthly;
      } else {
        this.offerId = '';
      }
      this.updateDivisorFromCurrency();
      this.updateDiscountLabel();
    }
  }

  private updateDivisorFromCurrency(): void {
    const decimals = typeof this.prices?.decimals === 'number' ? this.prices.decimals : undefined;
    if (decimals === 3) {
      this.minorUnitDivisor = 1000;
      return;
    }
    if (decimals === 2) {
      this.minorUnitDivisor = 100;
      return;
    }
    const currency = (this.prices?.currency || 'INR').toUpperCase();
    const threeDecimal = ['BHD', 'JOD', 'KWD', 'OMR', 'TND'];
    this.minorUnitDivisor = threeDecimal.includes(currency) ? 1000 : 100;
  }

  private updateDiscountLabel(): void {
    if (!this.prices?.planId) {
      this.discountLabel = '';
      return;
    }
    const key = `${this.prices.planId}|${this.offerId || ''}`;
    if (this.lastPreviewKey === key) return;
    this.lastPreviewKey = key;
    // Prefer preview to compute accurate discount percent
    this.homeService.preview(this.prices.planId, this.offerId || '').subscribe((p: any) => {
      const amount = typeof p?.amount === 'number' && p.amount > 0 ? p.amount : this.prices?.amount;
      const discount = typeof p?.discount === 'number' ? p.discount : 0;
      if (amount) {
        const pct = Math.round((discount / amount) * 100);
        if (pct > 0) {
          const planType = (this.pos || this.prices?.period || 'MONTHLY').toUpperCase();
          const planText = planType === 'YEARLY' ? 'annual' : 'monthly';
          this.discountLabel = `${pct}% off on subscribing on ${planText} plan.`;
        }
        this.originalAmountMinor = amount;
        this.discountedPayableMinor = Math.max(0, amount - discount);
      }
    });
  }

  moreDetails() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['terms-and-conditions'])
    );
    window.open(url + '?terms=pricing', '_blank');
  }

  monthYearlyToggle(): void {
    this.togglePlan.emit(this.pos);
    // Update offer based on selected period
    const envYearly = (window as any)?.__env?.YEARLY_OFFER_ID || (environment as any)?.YEARLY_OFFER_ID;
    const envMonthly = (window as any)?.__env?.MONTHLY_OFFER_ID || (environment as any)?.MONTHLY_OFFER_ID;
    if (this.pos === 'YEARLY' && envYearly) {
      this.offerId = envYearly;
    } else if (this.pos === 'MONTHLY' && envMonthly) {
      this.offerId = envMonthly;
    } else {
      this.offerId = '';
    }
    // Refresh the discount label text for the newly selected plan
    this.lastPreviewKey = '';
    this.updateDiscountLabel();
  }

  buyNow(id = null): void {
    if (!this.userDetails) {
      this.router.navigate(['/login/employerLogin']);
    } else {
      this.ngxService.startLoader('loader-03');
      this.homeService.buyNow(id, this.offerId || undefined).subscribe((prices) => {
        this.paymentGatewayKeyId = prices?.key_id || this.paymentGatewayKeyId;
        this.razorPayCheckout(prices);
        this.ngxService.stopLoader('loader-03');
      }, (error) => {
        let err = error?.error?.errors[0].error?.description || error?.error?.errors[0].error;
        this.toastr.error(err, 'Error');
        this.ngxService.stopLoader('loader-03');
      })
    }
  }

  private razorPayCheckout(details: any): void {
    let razorPayOptions = {
      "key": this.paymentGatewayKeyId,
      "subscription_id": details.subscription_id,
      "name": "Procollab Private Ltd",
      "description": this.prices?.name || 'Subscription',
      "modal": {
        ondismiss: () => {
          this.ngZone.run(() => {
            try { this.toastr.info('Payment cancelled'); } catch { }
          });
        }
      },
      "handler": (response) => {
        this.ngZone.run(() => {
          let reqObj = {
            razorpay_signature: response.razorpay_signature,
            payment_id: response.razorpay_payment_id,
            subscription_id: details.subscription_id,
            razorpay_subscription_id: (response as any)?.razorpay_subscription_id || details.subscription_id
          }
          this.homeService.paymentValidation(reqObj).subscribe((res: any) => {
            this.router.navigate(['./dashboard/subscription-success']);
            this.toastr.success(toasterMessages.paymentSuccess, 'Sucess', { timeOut: 2000 });
            try {
              const prev = JSON.parse(localStorage.getItem('currentUser') || '{}');
              prev.paymentStatus = res?.paymentStatus || 'PAID';
              localStorage.setItem('currentUser', JSON.stringify(prev));
            } catch { }
            try { this.dataService.cancelSubscriptionClicked('Subscription Active'); } catch { }
          }, (error) => {
            let err = error?.error?.errors[0].error?.description || error?.error?.errors[0].error;
            this.toastr.error(err, 'Error');
          })
        });
      }
    };

    // const rzp = new this.winRef.nativeWindow.Razorpay(razorPayOptions);
    // rzp.open();
    const razPayInstance = new Razorpay(razorPayOptions);
    try {
      if (typeof (razPayInstance as any).on === 'function') {
        (razPayInstance as any).on('payment.failed', (resp: any) => {
          this.ngZone.run(() => {
            const reason = resp?.error?.description || resp?.error?.reason || 'Payment failed';
            this.toastr.error(reason, 'Payment Failed');
          });
        });
      }
    } catch { }
    razPayInstance.open();
  }

  onMouseMove(event: MouseEvent) {
    const card = (event.currentTarget as HTMLElement).closest('.pricing-card') as HTMLElement;
    if (card) {
      const rect = card.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    }
  }

}
